import React, { ReactNode, useCallback, useMemo } from 'react';

import { getAccHostname } from '@adsk/acs-sc-web-platform-api';
import * as analytics from '@adsk/acs-web-analytics';
import { useAlloyFeatureFlag } from '@adsk/alloy-provider';
import { safeSearchRegex } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';

import { factoryProjectEntitlementsKey } from './factoryProjectEntitlementsKey';
import useSearch from './useSearch';
import { ANALYTICS_TYPES } from '../helpers/analytics';
import { Account, Project } from '../helpers/types';
import {
  PROJECT_ENTITLEMENTS_CLASSIFICATION,
  PROJECT_ENTITLEMENTS_FIELDS,
  PROJECT_ENTITLEMENTS_LIMIT,
  TABS_NAMES,
  TEMPLATE_ENTITLEMENTS_CLASSIFICATION,
  TEMPLATE_ENTITLEMENTS_FIELDS,
  PROJECT_ENTITLEMENTS_V2_PATH,
  CLIENTS_PATH,
} from '../layout/consts';
import ProjectListAccountItem from '../layout/ProjectListAccountItem';
import ProjectListProjectItem from '../layout/ProjectListProjectItem';
import ProjectListTemplateItem from '../layout/ProjectListTemplateItem';
import ProjectRowLoader from '../layout/ProjectRowLoader';
import TemplateRowLoader from '../layout/TemplateRowLoader';

type UseProjectPickerTabsParams = {
  accounts?: Account[];
  currentTab: string;
  search?: string;
  hasAccounts: boolean;
  onClose?: () => void;
};

type RenderItemProps = {
  item: unknown;
  currentAccountId?: string;
  currentProductId?: string;
  currentProjectId?: string;
  onAccountClick?: (account?: object, url?: string) => void;
  onProjectClick?: (project?: object, url?: string) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
};

type ProjectPickerTab = {
  tab: string;
  label?: string;
  viewAllLabel?: string | null | ReactNode;
  viewAllLink?: string;
  loading: boolean;
  error: boolean;
  retry: () => void;
  totalResults?: number;
  data?: unknown[];
  fetchNextPage?: () => void;
  hasNextPage: boolean;
  renderLoader: () => JSX.Element;
  renderItem: (props: RenderItemProps) => JSX.Element;
};

export const useProjectPickerTabs = function ({
  search,
  currentTab,
  accounts,
  hasAccounts,
  onClose,
}: UseProjectPickerTabsParams): ProjectPickerTab[] {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const { value: slashClients, loading: slashClientsLoading } =
    useAlloyFeatureFlag('slash-clients');
  const {
    loading: viewAllLinksAtTopFFLoading,
    value: viewAllLinksAtTopFFValue,
  } = useAlloyFeatureFlag(
    'project-picker-view-all-projects-link-relocation-to-top',
  );
  const path = slashClients ? CLIENTS_PATH : PROJECT_ENTITLEMENTS_V2_PATH;

  const accountsSearch = useMemo(
    () =>
      (accounts &&
        accounts.filter((a) =>
          safeSearchRegex(search).test(a.account_display_name),
        )) ||
      [],
    [accounts, search],
  );

  const projectGetKeyMemo = useMemo(
    () =>
      factoryProjectEntitlementsKey({
        path,
        fields: PROJECT_ENTITLEMENTS_FIELDS,
        classification: PROJECT_ENTITLEMENTS_CLASSIFICATION,
        limit: PROJECT_ENTITLEMENTS_LIMIT,
      }),
    [path],
  );

  const {
    loading: projectsLoading,
    error: projectsError,
    data: projects,
    totalResults: projectsTotalResults,
    retry: projectsRetry,
    hasNextPage: hasNextProjectsPage,
    nextPage: nextProjectsPage,
  } = useSearch({
    enabled: !slashClientsLoading && currentTab === TABS_NAMES.PROJECTS,
    getKey: projectGetKeyMemo,
    search,
  });

  const templateGetKeyMemo = useMemo(
    () =>
      factoryProjectEntitlementsKey({
        path,
        fields: TEMPLATE_ENTITLEMENTS_FIELDS,
        classification: TEMPLATE_ENTITLEMENTS_CLASSIFICATION,
        limit: PROJECT_ENTITLEMENTS_LIMIT,
      }),
    [path],
  );

  const {
    loading: templatesLoading,
    error: templatesError,
    data: templates,
    totalResults: templatesTotalResults,
    retry: templatesRetry,
    hasNextPage: hasNextTemplatePage,
    nextPage: nextTemplatePage,
  } = useSearch({
    enabled: !slashClientsLoading && currentTab === TABS_NAMES.TEMPLATES,
    getKey: templateGetKeyMemo,
    search,
  });

  const handleAnalyticOnClick = useCallback((project?: Project) => {
    if (project) {
      const analyticsType =
        project.classification === TABS_NAMES.TEMPLATES
          ? ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_TEMPLATE_CLICK
          : ANALYTICS_TYPES.TOPNAVIGATION_PROJECTPICKER_PROJECT_CLICK;
      const data = {
        product_ids:
          project.products &&
          project.products.map((product) => product.key).join(', '),
        product_names:
          project.products &&
          project.products.map((product) => product.name).join(', '),
        project_id: project.id,
        project_name: project.name,
      };
      analytics.track(analyticsType, data);
    }
  }, []);

  return useMemo<ProjectPickerTab[]>(
    () => [
      ...(hasAccounts
        ? [
            {
              tab: TABS_NAMES.ACCOUNTS,
              label: T.translate('ACCOUNTS')?.toString(),
              loading: false,
              error: false,
              hasNextPage: false,
              totalResults: accountsSearch.length,
              data: accountsSearch,
              retry: () => null,
              renderLoader: () => <ProjectRowLoader />,
              renderItem: ({
                item,
                currentAccountId,
                currentProductId,
                onAccountClick,
                containerRef,
              }: RenderItemProps) => (
                <ProjectListAccountItem
                  key={(item as Account)?.account_id}
                  item={item as Account}
                  currentAccountId={currentAccountId}
                  currentProductId={currentProductId}
                  onClose={onClose}
                  onAccountClick={onAccountClick}
                  containerRef={containerRef}
                />
              ),
            },
          ]
        : []),
      {
        tab: TABS_NAMES.PROJECTS,
        label: T.translate('PROJECTS')?.toString(),
        viewAllLabel: T.translate(
          !viewAllLinksAtTopFFLoading && viewAllLinksAtTopFFValue
            ? 'PROJECT_PICKER_VIEW_ALL'
            : 'VIEW_ALL_PROJECTS',
        ),
        viewAllLink: `${getAccHostname()}/projects`,
        loading: projectsLoading,
        error: projectsError,
        retry: projectsRetry,
        totalResults: projectsTotalResults,
        data: projects,
        fetchNextPage: nextProjectsPage,
        hasNextPage: hasNextProjectsPage,
        renderLoader: () => <ProjectRowLoader />,
        renderItem: ({
          item,
          currentProjectId,
          currentProductId,
          onProjectClick,
          containerRef,
        }: RenderItemProps) => (
          <ProjectListProjectItem
            key={(item as Project)?.id}
            item={item as Project}
            currentProjectId={currentProjectId}
            currentProductId={currentProductId}
            onClose={onClose}
            onProjectClick={onProjectClick}
            handleAnalyticOnClick={handleAnalyticOnClick}
            containerRef={containerRef}
          />
        ),
      },
      {
        tab: TABS_NAMES.TEMPLATES,
        label: T.translate('TEMPLATES')?.toString(),
        viewAllLabel: T.translate(
          !viewAllLinksAtTopFFLoading && viewAllLinksAtTopFFValue
            ? 'PROJECT_PICKER_VIEW_ALL'
            : 'VIEW_ALL_TEMPLATES',
        ),
        viewAllLink: `${getAccHostname()}/projects/templates`,
        loading: templatesLoading,
        error: templatesError,
        retry: templatesRetry,
        totalResults: templatesTotalResults,
        data: templates,
        fetchNextPage: nextTemplatePage,
        hasNextPage: hasNextTemplatePage,
        renderLoader: () => <TemplateRowLoader />,
        renderItem: ({
          item,
          currentProjectId,
          currentProductId,
          onProjectClick,
          containerRef,
        }: RenderItemProps) => (
          <ProjectListTemplateItem
            key={(item as Project)?.id}
            item={item as Project}
            currentProjectId={currentProjectId}
            currentProductId={currentProductId}
            onClose={onClose}
            onProjectClick={onProjectClick}
            handleAnalyticOnClick={handleAnalyticOnClick}
            containerRef={containerRef}
          />
        ),
      },
    ],
    [
      T,
      accountsSearch,
      handleAnalyticOnClick,
      hasAccounts,
      hasNextProjectsPage,
      hasNextTemplatePage,
      nextProjectsPage,
      nextTemplatePage,
      onClose,
      projects,
      projectsError,
      projectsLoading,
      projectsRetry,
      projectsTotalResults,
      templates,
      templatesError,
      templatesLoading,
      templatesRetry,
      templatesTotalResults,
      viewAllLinksAtTopFFLoading,
      viewAllLinksAtTopFFValue,
    ],
  );
};
